import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import BrowserContainer from 'components/common/select/BrowserContainer';
import get from 'lodash.get';

export const WarrantyDataItemComponent = ({
  assets,
  warranty,
  loadWarrantyItems,
  warrantyTypes,
  t,
  loadPartners,
  partnerTypes,
  validationInfo,
  onPropertyChange,
  idx,
  checkFilters,
  partnerRoles,
  warrantyItemVisible,
  editRight,
  guarantors,
  userIsDealer
}) => {
  const [dictionaryWarrantyType, setDictionaryWarrantyType] = useState({
    warrantyTypeId: warranty.dictionaryWarrantyTypeId,
    warrantyTypeName: warranty.dictionaryWarrantyTypeName
  });

  const dictionaruWarrantyTypeChange = useCallback(
    value => {
      setDictionaryWarrantyType(value);
      onPropertyChange('dictionaryWarrantyTypeId')(value.warrantyTypeId);
    },
    [onPropertyChange]
  );

  const [warrantyType, setWarrantyType] = useState({
    warrantyTypeId: warranty.warrantyTypeId,
    warrantyTypeName: warranty.warrantyTypeName
  });

  const warrantyTypeChange = useCallback(
    value => {
      setWarrantyType(value);
      onPropertyChange('warrantyTypeId')(value.warrantyTypeId);
    },
    [onPropertyChange]
  );

  return (
    <Grid container spacing={1} mb={2}>
      {!warrantyItemVisible && (
        <>
          <Grid item xs={12} md={4} sm={6}>
            <Autocomplete
              id={'warrantyDataCard_' + idx + '_dictionaryWarrantyType'}
              fullWidth
              required
              value={dictionaryWarrantyType}
              onChange={dictionaruWarrantyTypeChange}
              label={t('Document.Warranty.WarrantyType')}
              valueKey='warrantyTypeId'
              labelKey='warrantyTypeName'
              options={warrantyTypes}
              error={Boolean(validationInfo.dictionaryWarrantyTypeId)}
              helperText={validationInfo.warrantyTypeId}
              disabled={!editRight}
            />
          </Grid>
        </>
      )}
      {warrantyItemVisible && (
        <>
          <Grid item xs={12} md={4} sm={6}>
            <Autocomplete
              id={'warrantyDataCard_' + idx + '_warrantyType'}
              fullWidth
              required
              value={warrantyType}
              onChange={warrantyTypeChange}
              label={t('Document.Warranty.WarrantyType')}
              valueKey='warrantyTypeId'
              labelKey='warrantyTypeName'
              options={warrantyTypes}
              error={Boolean(validationInfo.warrantyTypeId)}
              helperText={validationInfo.warrantyTypeId}
              disabled={!editRight}
            />
          </Grid>
          <Grid item xs={12} md={4} sm={6}>
            <Autocomplete
              id={'warrantyDataCard_' + idx + '_warrantyItem'}
              fullWidth
              value={warranty.warrantyItem || {}}
              onChange={onPropertyChange('warrantyItem')}
              label={t('Document.Warranty.WarrantyItem')}
              valueKey='itemId'
              labelKey='itemName'
              loadOptions={loadWarrantyItems}
              error={Boolean(validationInfo.warrantyItem)}
              helperText={validationInfo.warrantyItem}
              disabled={!editRight}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={'warrantyDataCard_' + idx + '_guaranteeNumber'}
          fullWidth
          name='guaranteeNumber'
          onChange={onPropertyChange('guaranteeNumber')}
          value={warranty.guaranteeNumber || ''}
          label={t('Document.Warranty.GuaranteeNumber')}
          helperText={validationInfo.guaranteeNumber}
          disabled={!editRight}
        />
      </Grid>
      {!warrantyItemVisible && <Grid item xs={12} md={4} sm={6} />}
      <Grid item xs={12} md={4} sm={6}>
        <Autocomplete
          id={'warrantyDataCard_' + idx + '_issuerType'}
          fullWidth
          required
          isClearable={false}
          value={warranty.issuerTypeId || ''}
          onChange={onPropertyChange('issuerTypeId')}
          error={Boolean(validationInfo.issuerTypeId)}
          helperText={validationInfo.issuerTypeId}
          label={t('Document.Warranty.IssuerType')}
          valueKey='partnerRoleId'
          simpleValue
          name='partnerRoleId'
          options={partnerRoles}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <BrowserContainer
          required
          selectedValue={{
            value: get(warranty, 'issuer.issuerName', ''),
            id: get(warranty, 'issuer.issuerId', '')
          }}
          textFieldDisabled
          error={Boolean(validationInfo.issuer)}
          helperText={validationInfo.issuer}
          checkFilters={checkFilters}
          label={t('Document.Warranty.Issuer')}
          columns={[
            { name: 'partnerId', title: 'PartnerId' },
            { name: 'partnerCode', title: t('OfferEdit.Client.Code') },
            { name: 'partnerName', title: t('OfferEdit.Client.Name') },
            { name: 'uniqueId', title: t('OfferEdit.Client.PersonalIdentifier') },
            {
              name: 'partnerTypeId',
              title: t('OfferEdit.Client.PartnerType'),
              isAutocomplete: 'true',
              autocompleteOptions: partnerTypes,
              id: 'partnerTypeId',
              value: 'partnerTypeName',
              getCellValue: row => (row.partnerType ? row.partnerType.partnerTypeName : undefined)
            }
          ]}
          sortingColumns={[{ columnName: 'partnerId', direction: 'desc' }]}
          loadOptions={loadPartners}
          filterColumns={[
            { columnName: 'partnerCode' },
            { columnName: 'partnerName' },
            { columnName: 'partnerTypeId' },
            { columnName: 'uniqueId' }
          ]}
          hiddenColumnNames={['partnerId']}
          idColumnName={'partnerId'}
          valueColumnName={'partnerName'}
          onChange={onPropertyChange('issuer')}
          iconDisabled={!editRight}
          iconHidden={userIsDealer}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
      <Autocomplete
          id={'warrantyDataCard_' + idx + '_guarantor'}
          fullWidth
          value={warranty.guarantor || {}}
          isClearable={false}
          onChange={onPropertyChange('guarantor')}
          label={t('Document.Warranty.Guarantor')}
          valueKey='guarantorId'
          labelKey='guarantorName'
          options={guarantors}
          disabled={!editRight || !warranty.enableGuarantor}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          required
          id={'warrantyDataCard_' + idx + '_qtty'}
          fullWidth
          isNumeric
          name='qtty'
          onChange={onPropertyChange('qtty')}
          value={warranty.qtty || ''}
          label={t('Document.Warranty.Qtty')}
          error={Boolean(validationInfo.qtty)}
          helperText={validationInfo.qtty}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={'warrantyDataCard_' + idx + '_amount'}
          fullWidth
          name='amount'
          isNumeric
          onChange={onPropertyChange('amount')}
          value={warranty.amount}
          label={t('Document.Warranty.Amount')}
          error={Boolean(validationInfo.amount)}
          helperText={validationInfo.amount}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={'warrantyDataCard_' + idx + '_currency'}
          fullWidth
          disabled={true}
          value={warranty.currencyCode || ''}
          label={t('Document.Warranty.Currency')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <DateTime
          id={'warrantyDataCard_' + idx + '_validFrom'}
          fullWidth
          value={warranty.validFrom}
          label={t('Document.Warranty.ValidFrom')}
          onChange={onPropertyChange('validFrom')}
          timeFormat={false}
          error={Boolean(validationInfo.validFrom)}
          helperText={validationInfo.validFrom}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <DateTime
          id={'warrantyDataCard_' + idx + '_validTo'}
          fullWidth
          value={warranty.validTo}
          label={t('Document.Warranty.ValidTo')}
          onChange={onPropertyChange('validTo')}
          timeFormat={false}
          error={Boolean(validationInfo.validTo)}
          helperText={validationInfo.validTo}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <Autocomplete
          id={'warrantyDataCard_' + idx + '_asset'}
          fullWidth
          value={warranty.documentDetail || {}}
          isClearable={false}
          onChange={onPropertyChange('documentDetail')}
          label={t('Document.Warranty.Asset')}
          valueKey='documentDetailId'
          labelKey='documentDetailName'
          options={assets}
          disabled={!editRight}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={'warrantyDataCard_' + idx + '_comments'}
          fullWidth
          name='freeTxt'
          value={warranty.freeTxt || ''}
          onChange={onPropertyChange('freeTxt')}
          label={t('Document.Warranty.Comments')}
          disabled={!editRight}
          inputProps={{ maxLength: 255 }}
        />
      </Grid>
    </Grid>
  );
};

WarrantyDataItemComponent.propTypes = {
  warranty: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  checkFilters: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  validationInfo: PropTypes.object.isRequired,
  assets: PropTypes.array.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  loadPartners: PropTypes.func.isRequired,
  loadWarrantyItems: PropTypes.func.isRequired,
  idx: PropTypes.number.isRequired,
  warrantyTypes: PropTypes.array.isRequired,
  partnerRoles: PropTypes.array.isRequired,
  warrantyItemVisible: PropTypes.bool,
  editRight: PropTypes.bool.isRequired,
  guarantors: PropTypes.array.isRequired,
  userIsDealer: PropTypes.bool.isRequired
};

export default compose(withTranslation())(WarrantyDataItemComponent);
